// context/IntercomContext.tsx
'use client';

import logger from '@/layers/support/monitoring/logger';
import { IntercomInstance, IntercomSettings } from '@intercom/messenger-js-sdk/dist/types';
import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useGlobalState } from './GlobalStateContext';

const LOG_MODULE = '[Presentation / Context / Intercom]';

interface Window {
  intercomSettings?: IntercomSettings;
  Intercom: IntercomInstance | undefined;
  attachEvent: Function;
}

interface IntercomContextProps {
  showIntercom: () => void;
  hideIntercom: () => void;
  updateIntercom: (data: Record<string, any>) => void;
  getVisitorId: () => Promise<string | undefined>;
  triggerFeedback: (data: Record<string, any>) => void;
}

const IntercomContext = createContext<IntercomContextProps | undefined>(undefined);

const INTERCOM_APP_ID = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

interface IntercomUser {
  type?: string;
  personal_info?: {
    email?: string;
  };
}

interface IntercomProfile {
  id?: string;
  created_at?: string | Date | null;
}

const IntercomProviderBase: React.FC<{
  children: React.ReactNode;
  user?: IntercomUser | null;
  profile?: IntercomProfile | null;
}> = ({ children, user, profile }) => {
  const intercomScriptRequestedRef = useRef(false);
  const intercomBootedRef = useRef(false);

  const toUnixSeconds = useCallback((value: string | Date | null | undefined) => {
    if (!value) return undefined;
    if (value instanceof Date) return Math.floor(value.getTime() / 1000);
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
  }, []);

  const ensureIntercomStub = useCallback(() => {
    if (typeof window === 'undefined') return;
    const w = window as any;

    if (typeof w.Intercom === 'function') return;

    const i = function () {
      i.c(arguments);
    } as any;
    i.q = [];
    i.c = function (args: any) {
      i.q.push(args);
    };
    w.Intercom = i as IntercomInstance;
  }, []);

  const requestIntercomScript = useCallback(() => {
    if (!INTERCOM_APP_ID) return;
    if (intercomScriptRequestedRef.current) return;
    intercomScriptRequestedRef.current = true;

    // Ensure calls are queued even before script loads.
    ensureIntercomStub();

    if (typeof document === 'undefined') return;
    if (document.getElementById('intercom-script')) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'intercom-script';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  }, [ensureIntercomStub]);

  const bootOrUpdate = useCallback(
    (settings: IntercomSettings) => {
      ensureIntercomStub();
      if (typeof window === 'undefined') return;
      if (!window.Intercom) return;

      if (!intercomBootedRef.current) {
        window.Intercom('boot', settings);
        intercomBootedRef.current = true;
        return;
      }

      window.Intercom('update', settings);
    },
    [ensureIntercomStub],
  );

  useEffect(() => {
    logger.debug(`${LOG_MODULE} IntercomProvider (deferred load)`, {
      INTERCOM_APP_ID,
      user,
      profile,
    });

    if (!INTERCOM_APP_ID) {
      console.error('Intercom App ID is not defined');
      return;
    }

    // Install queue stub so update/show calls are buffered, but defer the actual script download.
    ensureIntercomStub();

    const load = () => requestIntercomScript();
    const events: Array<keyof WindowEventMap> = ['scroll', 'click', 'touchstart', 'keydown'];
    events.forEach(eventName =>
      window.addEventListener(eventName, load, { once: true, passive: true }),
    );
    const timeout = window.setTimeout(load, 5000);

    return () => {
      events.forEach(eventName => window.removeEventListener(eventName, load as any));
      window.clearTimeout(timeout);
    };
  }, [profile, user, ensureIntercomStub, requestIntercomScript]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.Intercom) {
        window.Intercom('shutdown');
        intercomBootedRef.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!INTERCOM_APP_ID) return;

    if (user && profile && profile.id) {
      logger.debug(`${LOG_MODULE} bootOrUpdate (identified)`, { user, profile });
      bootOrUpdate({
        app_id: INTERCOM_APP_ID,
        user_id: profile.id,
        name: profile.id,
        email: user.personal_info?.email,
        created_at: toUnixSeconds(profile.created_at),
        'User Type': user.type,
      } as IntercomSettings);
      return;
    }

    logger.debug(`${LOG_MODULE} bootOrUpdate (anonymous)`);
    bootOrUpdate({ app_id: INTERCOM_APP_ID } as IntercomSettings);
  }, [user, profile, bootOrUpdate, toUnixSeconds]);

  const getVisitorId = async (): Promise<string | undefined> => {
    logger.debug(`${LOG_MODULE} getVisitorId`, { Intercom: window.Intercom });
    if (window.Intercom) {
      const visitorId = window.Intercom('getVisitorId');
      logger.debug(`${LOG_MODULE} Visitor ID`, { visitorId });
      return visitorId;
    } else {
      logger.warn(`${LOG_MODULE} Intercom is not initialized.`);
      return undefined;
    }
  };

  const showIntercom = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  const hideIntercom = () => {
    if (window.Intercom) {
      window.Intercom('hide');
    }
  };

  const updateIntercom = (data: Record<string, any>) => {
    if (window.Intercom) {
      window.Intercom('update', data);
    }
  };

  const triggerFeedback = (data: Record<string, any>) => {
    logger.debug(`${LOG_MODULE} triggerFeedback`, { data });

    if (window.Intercom && typeof window.Intercom === 'function') {
      if (!data.elementInfo) {
        logger.error(`${LOG_MODULE} Missing 'elementInfo' in feedback data.`);
        return;
      }

      // Construct the message as plain text
      const message =
        `Your comment here: ...\n\n` +
        `📄 Text Content:\n${data.elementInfo.text}\n\n` +
        `🛠 Reported Element:\n` +
        `${data.elementInfo.tag}${data.elementInfo.id ? `#${data.elementInfo.id}` : ''}${data.elementInfo.classes ? `.${data.elementInfo.classes.split(' ').join('.')}` : ''}`;

      // Open Intercom messenger with pre-filled message
      showIntercom();
      window.Intercom('showNewMessage', message);
    } else {
      logger.warn(`${LOG_MODULE} Intercom is not initialized.`);
    }
  };

  return (
    <IntercomContext.Provider
      value={{
        showIntercom,
        hideIntercom,
        updateIntercom,
        getVisitorId,
        triggerFeedback,
      }}
    >
      {children}
    </IntercomContext.Provider>
  );
};

export const IntercomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile } = useGlobalState();
  return (
    <IntercomProviderBase user={user} profile={profile}>
      {children}
    </IntercomProviderBase>
  );
};

export const MarketingIntercomProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <IntercomProviderBase>{children}</IntercomProviderBase>;
};

export const useIntercom = (): IntercomContextProps => {
  const context = useContext(IntercomContext);
  if (context === undefined) {
    throw new Error('useIntercom must be used within an IntercomProvider');
  }
  return context;
};
